import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import api from '../../services/api';
import { normalize } from '../../utils/normalize';
import PackIcon from '../PackIcon';
import './InlineConsolidator.css';

const DROPDOWN_EST = 280;
const DROPDOWN_MIN_W = 320;
const GAP = 4;
const EDGE_PAD = 8;
const MOBILE_BREAKPOINT = 640;
const getVVHeight = () => window.visualViewport?.height ?? window.innerHeight;

export default function InlineConsolidator({ checklistItemId, links, onLinksChange, itemName, isActive = false, noChips = false, collapsible = false, open, onOpenChange, onConsolidated, onArrowOut, onActivate, consolidateOverride, committedIds, crossConflicts, eventId }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [qtys, setQtys] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dropdownRect, setDropdownRect] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const aiFetchedFor = useRef(null);

  useEffect(() => {
    if (isActive && inputRef.current) inputRef.current.focus();
  }, [isActive]);

  // `expanded` puede ser controlado por el padre (prop `open`) o interno.
  const isExpanded = open !== undefined ? open : expanded;

  // IA: al abrir el buscador de un ítem sin equipo asignado, sugiere sola
  // hasta 6 equipos del depósito a partir del nombre del ítem. 1 clic = asignado.
  useEffect(() => {
    if (!isExpanded || !itemName || links.length > 0) return;
    if (aiFetchedFor.current === itemName) return;
    aiFetchedFor.current = itemName;
    setAiLoading(true);
    api.post('/ai/match-equipment', { text: itemName })
      .then(({ data }) => {
        const linked = new Set(links.map(l => (l.item_name || l.pack_name || '').toLowerCase()));
        setAiSuggestions(
          (data.materials || [])
            .map(m => m.name)
            .filter(n => !linked.has(n.toLowerCase()))
            .slice(0, 6)
        );
      })
      .catch(() => setAiSuggestions([]))
      .finally(() => setAiLoading(false));
  }, [isExpanded, itemName, links]);

  // Modo colapsable: al expandir, enfocar el input
  useEffect(() => {
    if (isExpanded && inputRef.current) inputRef.current.focus();
  }, [isExpanded]);

  const collapse = () => {
    if (onOpenChange) onOpenChange(false); else setExpanded(false);
    setQuery(''); setSuggestions([]); setQtys({}); setActiveIndex(-1);
  };

  // Debounced search
  useEffect(() => {
    if (query.length < 2) { setSuggestions([]); setQtys({}); setActiveIndex(-1); return; }
    const t = setTimeout(async () => {
      try {
        const [itemsRes, packsRes] = await Promise.all([
          // Límite alto: el backend devuelve UNIDADES y el front agrupa por
          // nombre. Con un límite bajo, unidades de pocos ítems populares
          // consumían el cupo y ocultaban otros nombres (ej. "pls" → 140
          // unidades de 7 nombres, pero solo entraban 3).
          api.get(`/inventory/items?search=${encodeURIComponent(query)}&limit=600`),
          api.get(`/inventory/packs?search=${encodeURIComponent(query)}&limit=100`),
        ]);

        const alreadyLinkedIds = new Set(links.map(l => l.inventory_item_id).filter(Boolean));
        const alreadyLinkedPackIds = new Set(links.map(l => l.pack_id).filter(Boolean));
        const committed = committedIds instanceof Set ? committedIds : new Set();
        // No excluimos las ya linkeadas del grupo: así el equipo sigue apareciendo
        // en el buscador aunque no queden unidades libres (se muestra en amarillo).
        const items = itemsRes.data.filter(i => !i.pack_id && i.is_operational);

        const qWords = normalize(query).split(/\s+/).filter(Boolean);
        const matchQ = name => qWords.every(w => normalize(name).includes(w));

        let dynamicCross = {};
        if (eventId) {
          try {
            const r = await api.get(`/inventory/item-cross-conflicts?event_id=${eventId}&search=${encodeURIComponent(query)}`);
            dynamicCross = r.data || {};
          } catch { /* best-effort — no bloquea la búsqueda */ }
        }
        const extCross = { ...(crossConflicts || {}), ...dynamicCross };
        const groupMap = new Map();
        for (const item of items) {
          if (!matchQ(item.name)) continue;
          if (!groupMap.has(item.name)) groupMap.set(item.name, { type: 'group', name: item.name, atHome: 0, freeHome: 0, operativas: 0 });
          const entry = groupMap.get(item.name);
          entry.operativas++; // total operativas (is_operational), estén o no en depósito
          if (item.is_at_home) {
            entry.atHome++;
            // Las unidades ya consolidadas en este ítem no cuentan como libres.
            if (!committed.has(item.id) && !alreadyLinkedIds.has(item.id)) entry.freeHome++;
          }
        }
        // Conteos REALES por nombre (del backend item-cross-conflicts, sin el
        // límite del search): inventario = total operativas; committed = las que
        // usan OTROS eventos solapados; own = las ya asignadas en ESTE evento
        // (todas sus líneas). disponibles = libres en la ventana.
        // Fallback si no hay eventId: contar los links del ítem actual.
        const asignadoByName = {};
        for (const l of links) { const n = l.item_name || l.pack_name; if (n) asignadoByName[n] = (asignadoByName[n] || 0) + 1; }
        for (const [name, entry] of groupMap) {
          const cross = extCross[name];
          const isObj = cross && typeof cross === 'object';
          const committedOthers = isObj ? (cross.committed || 0) : (cross || 0);
          entry.inventario = (isObj && cross.inventario != null) ? cross.inventario : entry.operativas;
          entry.asignadoEvento = (isObj && cross.own != null) ? cross.own : (asignadoByName[name] || 0);
          // Capacidad del evento en la ventana = stock total − lo que usan otros
          // eventos solapados. Los Disponibles reales restan además lo asignado
          // en ESTE evento (own + lo que se está por llevar), en el render.
          entry.capacidad = Math.max(0, entry.inventario - committedOthers);
          entry.freeHome = Math.max(0, entry.freeHome - committedOthers);
        }

        const packs = packsRes.data
          .filter(p => p.is_operational && !alreadyLinkedPackIds.has(p.id) && matchQ(p.name))
          .map(p => {
            // Pack único → inventario 1. Mismos campos que un grupo para que se
            // calcule y muestre Asignado/Disponible/conflicto idéntico a una unidad.
            const cross = extCross[p.name];
            const isObj = cross && typeof cross === 'object';
            const committedOthers = isObj ? (cross.committed || 0) : 0;
            return {
              type: 'pack', id: p.id, name: p.name, itemCount: p.item_count ?? 0,
              inventario: 1,
              asignadoEvento: isObj ? (cross.own || 0) : 0,
              capacidad: Math.max(0, 1 - committedOthers),
            };
          });

        // Orden: primero libres, después ocupados; y dentro de cada grupo, primero
        // los packs y después los items sueltos. Desempate por nombre.
        const combined = [...groupMap.values(), ...packs];
        const isFree = s => ((s.capacidad || 0) - (s.asignadoEvento || 0)) > 0;
        combined.sort((a, b) => {
          const fa = isFree(a) ? 0 : 1, fb = isFree(b) ? 0 : 1;
          if (fa !== fb) return fa - fb;
          const pa = a.type === 'pack' ? 0 : 1, pb = b.type === 'pack' ? 0 : 1;
          if (pa !== pb) return pa - pb;
          return String(a.name).localeCompare(String(b.name), 'es');
        });
        setSuggestions(combined);
        setQtys({});
        setActiveIndex(-1);
      } catch (e) { console.error(e); }
    }, 250);
    return () => clearTimeout(t);
  }, [query]); // eslint-disable-line

  const showDropdown = suggestions.length > 0 && query.length >= 2;

  const updateRect = useCallback(() => {
    if (!inputRef.current) return;
    const r = inputRef.current.getBoundingClientRect();
    const vvHeight = getVVHeight();
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;

    if (isMobile) {
      // Bottom sheet: full-width, anclado justo encima del teclado virtual
      const bottomOffset = Math.max(0, window.innerHeight - vvHeight);
      setDropdownRect({ mobile: true, maxHeight: Math.max(80, Math.floor(vvHeight * 0.5)), bottomOffset });
      return;
    }

    // El input puede ser angosto (vista de equipos/checklist) pero el dropdown
    // necesita ancho para renderizar nombre + meta + botones de cantidad.
    const vvWidth = window.innerWidth;
    const width = Math.min(Math.max(r.width, DROPDOWN_MIN_W), vvWidth - EDGE_PAD * 2);
    const left = Math.max(EDGE_PAD, Math.min(r.left, vvWidth - EDGE_PAD - width));

    const spaceBelow = vvHeight - r.bottom - EDGE_PAD;
    const spaceAbove = r.top - EDGE_PAD;
    if (spaceBelow >= DROPDOWN_EST || spaceBelow >= spaceAbove) {
      setDropdownRect({ top: r.bottom + GAP, left, width,
                        maxHeight: Math.max(80, Math.min(DROPDOWN_EST, spaceBelow)) });
    } else {
      setDropdownRect({ bottom: vvHeight - r.top + GAP, left, width,
                        maxHeight: Math.max(80, Math.min(DROPDOWN_EST, spaceAbove)) });
    }
  }, []);

  useEffect(() => {
    if (showDropdown) updateRect();
  }, [showDropdown, updateRect]);

  useEffect(() => {
    if (!showDropdown) return;
    window.addEventListener('scroll', updateRect, { capture: true, passive: true });
    window.addEventListener('resize', updateRect);
    window.visualViewport?.addEventListener('resize', updateRect);
    window.visualViewport?.addEventListener('scroll', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, { capture: true });
      window.removeEventListener('resize', updateRect);
      window.visualViewport?.removeEventListener('resize', updateRect);
      window.visualViewport?.removeEventListener('scroll', updateRect);
    };
  }, [showDropdown, updateRect]);

  // Scroll active suggestion into view
  useEffect(() => {
    if (activeIndex >= 0 && dropdownRef.current) {
      dropdownRef.current.children[activeIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  // Close on outside click
  useEffect(() => {
    if (!showDropdown) return;
    const handler = (e) => {
      if (!inputRef.current?.contains(e.target) && !dropdownRef.current?.contains(e.target)) {
        setSuggestions([]); setQtys({}); setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDropdown]);

  // Modo colapsable: click afuera CIERRA la caja aunque haya texto tipeado.
  // Sin esto, el onBlur (que exige !query) dejaba el buscador colgado abierto.
  // El límite "adentro" es la celda de stock completa (badge incluido) para no
  // pelearse con el toggle del badge, más el dropdown (portal fuera del árbol).
  const collapseRef = useRef(null);
  collapseRef.current = collapse;
  useEffect(() => {
    if (!collapsible || !isExpanded) return;
    const handler = (e) => {
      const boundary = inputRef.current?.closest('.ep-item-stock, .cl-item-stock') || inputRef.current;
      if (!boundary?.contains(e.target) && !dropdownRef.current?.contains(e.target)) {
        collapseRef.current?.();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [collapsible, isExpanded]);

  // Navegación por teclado: mientras se usan las flechas, el puntero queda
  // deshabilitado sobre las filas (si no, el scroll de la lista bajo el mouse
  // quieto dispara mouseenter y el foco salta a donde está el cursor). Se
  // reactiva SOLO con movimiento físico real (mousemove no dispara por scroll).
  const [kbNav, setKbNav] = useState(false);

  // Indicador de scroll del dropdown: cuántas sugerencias quedan ocultas
  // arriba/abajo del viewport de la lista (pill "N más" pegada al borde).
  const [scrollHint, setScrollHint] = useState({ above: 0, below: 0 });
  const updateScrollHint = useCallback(() => {
    const el = dropdownRef.current;
    if (!el) return;
    const rows = el.querySelectorAll('.ic-suggestion');
    let above = 0, below = 0;
    const top = el.scrollTop, bottom = el.scrollTop + el.clientHeight;
    rows.forEach(r => {
      if (r.offsetTop + r.offsetHeight <= top + 2) above++;
      else if (r.offsetTop >= bottom - 2) below++;
    });
    setScrollHint(prev => (prev.above === above && prev.below === below) ? prev : { above, below });
  }, []);
  useEffect(() => {
    if (!showDropdown) return;
    const raf = requestAnimationFrame(updateScrollHint);
    return () => cancelAnimationFrame(raf);
  }, [showDropdown, suggestions, updateScrollHint]);

  // Per-suggestion qty helpers — la cantidad arranca en 1 y sube con "+"
  const sugKey = s => s.type === 'pack' ? `pack-${s.id}` : `group-${s.name}`;
  const getQty = s => qtys[sugKey(s)] || 0;
  const setQtyFor = (s, fn) => setQtys(prev => {
    const key = sugKey(s);
    const cur = prev[key] || 0;
    return { ...prev, [key]: typeof fn === 'function' ? fn(cur) : fn };
  });

  const consolidate = async (sug, quantity) => {
    if (saving) return;
    setSaving(true); setError('');
    try {
      let newLinks;
      if (consolidateOverride) {
        newLinks = await consolidateOverride(sug, quantity, links);
      } else if (sug.type === 'pack') {
        const { data } = await api.post(`/links/checklist-item/${checklistItemId}`, { pack_id: sug.id });
        newLinks = [...links, data];
      } else {
        const { data } = await api.post(`/links/checklist-item/${checklistItemId}/bulk`, {
          item_name: sug.name, quantity: quantity || 1,
        });
        newLinks = [...links, ...(data.links || [])];
      }
      onLinksChange(newLinks);
      setQuery(''); setSuggestions([]); setQtys({}); setActiveIndex(-1);
      onConsolidated?.();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al consolidar');
    } finally { setSaving(false); }
  };

  const removeLinks = async (toRemove) => {
    try {
      await Promise.all(toRemove.map(l => api.delete(`/links/checklist-item/${checklistItemId}/${l.id}`)));
      const ids = new Set(toRemove.map(l => l.id));
      onLinksChange(links.filter(l => !ids.has(l.id)));
    } catch (e) { setError(e.response?.data?.error || 'Error al quitar'); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      if (!showDropdown) { e.preventDefault(); onArrowOut?.('down'); return; }
      e.preventDefault();
      setKbNav(true);
      setActiveIndex(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      if (!showDropdown) { e.preventDefault(); onArrowOut?.('up'); return; }
      e.preventDefault();
      setKbNav(true);
      setActiveIndex(i => Math.max(i - 1, -1));
    } else if (showDropdown && activeIndex >= 0 && suggestions[activeIndex]?.type === 'group') {
      const s = suggestions[activeIndex];
      if (e.key === 'ArrowRight') { e.preventDefault(); setKbNav(true); setQtyFor(s, q => q + 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); setKbNav(true); setQtyFor(s, q => Math.max(q - 1, 0)); }
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && suggestions[activeIndex]) {
        const s = suggestions[activeIndex];
        consolidate(s, s.type === 'group' ? Math.max(getQty(s), 1) : 1);
        // Modo colapsable (equipos/checklist): tras consolidar por teclado,
        // cerrar la caja. El re-render por onConsolidated desenfoca el input y
        // el onBlur no alcanza a cerrarla → hay que cerrarla explícitamente.
        if (collapsible) collapse();
      } else if (collapsible) {
        // Enter sin sugerencia seleccionada: cerrar la caja en vez de dejarla
        // abierta y sin foco (que era imposible de cerrar con click afuera).
        collapse();
      } else {
        setQuery(''); setSuggestions([]); setQtys({}); setActiveIndex(-1);
        onArrowOut?.('down');
      }
    } else if (e.key === 'Escape') {
      if (collapsible) collapse();
      else { setQuery(''); setSuggestions([]); setQtys({}); setActiveIndex(-1); }
    }
  };

  const groupedLinks = useMemo(() => {
    const map = new Map();
    for (const l of links) {
      const key = l.pack_name || l.item_name || '';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(l);
    }
    return [...map.entries()].map(([label, group]) => ({ label, group }));
  }, [links]);

  // Los chips de IA se filtran contra los links VIGENTES en cada render — si el
  // equipo ya se vinculó por el buscador normal, su chip desaparece (evita
  // vincular una unidad de más con un chip obsoleto).
  const visibleAiSuggestions = useMemo(() => {
    const linked = new Set(links.map(l => (l.item_name || l.pack_name || '').toLowerCase()));
    return aiSuggestions.filter(n => !linked.has(n.toLowerCase()));
  }, [aiSuggestions, links]);

  return (
    <div className="ic-root" onClick={e => e.stopPropagation()}>
      {!noChips && groupedLinks.length > 0 && (
        <div className="ic-chips">
          {groupedLinks.map(({ label, group }) => (
            <span key={label} className="ic-chip">
              {group.length > 1 && (
                <span className="ic-chip-count">{group.length} × </span>
              )}
              <span className="ic-chip-label">{label}</span>
              <button
                className="ic-chip-x"
                onMouseDown={e => { e.preventDefault(); removeLinks(group); }}
                title="Quitar"
              >✕</button>
            </span>
          ))}
        </div>
      )}

      {collapsible && !isExpanded ? (
        // En modo controlado (open definido) el disparador es externo (el badge),
        // así que no se renderiza el botón "+".
        open === undefined ? (
          <button
            className="ic-add-btn"
            onClick={() => setExpanded(true)}
            title="Buscar en inventario"
            aria-label="Buscar en inventario"
          >+</button>
        ) : null
      ) : (
        <div className="ic-search-wrap">
          <input
            ref={inputRef}
            className="ic-input"
            placeholder={links.length > 0 ? 'Agregar más…' : 'Buscar en inventario…'}
            value={query}
            onChange={e => { setQuery(e.target.value); setActiveIndex(-1); }}
            onKeyDown={handleKeyDown}
            onFocus={() => onActivate?.()}
            onBlur={() => {
              // saving deshabilita el input y Chrome dispara blur — sin este guard,
              // clickear un chip de IA colapsaría el buscador a mitad de la acción.
              if (collapsible && !query && !saving) collapse();
            }}
            autoComplete="off"
            disabled={saving}
          />
        </div>
      )}

      {isExpanded && !query && aiLoading && (
        <div className="ic-ai-loading">La IA está buscando equipo…</div>
      )}
      {isExpanded && !query && !aiLoading && visibleAiSuggestions.length > 0 && (
        <div className="ic-ai-suggestions">
          {visibleAiSuggestions.map(name => (
            <button
              key={name}
              type="button"
              className="ic-ai-chip"
              disabled={saving}
              onMouseDown={e => {
                e.preventDefault();
                consolidate({ type: 'group', name }, 1);
                setAiSuggestions(prev => prev.filter(n => n !== name));
              }}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 3l1.9 5.8a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-1.9a2 2 0 0 0 1.3-1.3L12 3z"/>
              </svg>
              {name}
            </button>
          ))}
        </div>
      )}

      {error && <div className="ic-error">{error}</div>}

      {showDropdown && dropdownRect && ReactDOM.createPortal(
        <div
          className={`ic-dropdown${dropdownRect.mobile ? ' ic-dropdown--mobile' : ''}${kbNav ? ' ic-dropdown--kbnav' : ''}`}
          ref={dropdownRef}
          onMouseMove={kbNav ? () => setKbNav(false) : undefined}
          style={{
            maxHeight: dropdownRect.maxHeight,
            ...(dropdownRect.mobile
              ? { bottom: dropdownRect.bottomOffset, left: 0, right: 0 }
              : {
                  left: dropdownRect.left,
                  width: dropdownRect.width,
                  ...(dropdownRect.bottom !== undefined
                    ? { bottom: dropdownRect.bottom, top: 'auto' }
                    : { top: dropdownRect.top, bottom: 'auto' }),
                }),
          }}
          onScroll={updateScrollHint}
        >
          {scrollHint.above > 0 && (
            <div className="ic-scroll-hint ic-scroll-hint--top" aria-hidden="true">
              ▴ {scrollHint.above} más
            </div>
          )}
          {suggestions.map((s, idx) => {
            const isActive = idx === activeIndex;
            const currentQty = getQty(s);
            // Disponibles = capacidad − (lo asignado en ESTE evento + lo que voy a
            // llevar ahora). Dinámico: baja con el ×N. El nº de asignados no se
            // muestra (es redundante con el ×N de la derecha).
            const asignadoTotal = (s.asignadoEvento || 0) + currentQty;
            const disponibles = Math.max(0, (s.capacidad || 0) - asignadoTotal);
            // Amarillo si el ítem no tiene NADA disponible de entrada, o si el
            // ×N EXCEDE la capacidad. Llenar justo (20 de 20) no es conflicto —
            // el conflicto empieza al pedir 1 de más.
            const warnRow = (s.capacidad || 0) === 0 || asignadoTotal > (s.capacidad || 0);
            return (
              <div
                key={sugKey(s)}
                className={`ic-suggestion${isActive ? ' active' : ''}${warnRow ? ' ic-suggestion--warn' : ''}`}
                onMouseDown={e => { e.preventDefault(); consolidate(s, s.type === 'group' ? Math.max(currentQty, 1) : 1); }}
                onMouseEnter={() => setActiveIndex(idx)}
              >
                <div className="ic-sug-info">
                  <span className="ic-sug-name">{s.type === 'pack' && <PackIcon />}{s.name}</span>
                  <span className={`ic-sug-meta${warnRow ? ' ic-sug-meta--warn' : ''}`}>
                    {s.inventario > 0
                      ? `${disponibles} Disponible${disponibles === 1 ? '' : 's'} (${s.inventario} en Inventario)`
                      : 'Sin stock'}
                  </span>
                </div>
                {s.type === 'group' && s.atHome > 0 && (
                  <div className="ic-sug-qty-row" onMouseDown={e => e.stopPropagation()}>
                    <button
                      className="ic-qty-btn"
                      onMouseDown={e => { e.preventDefault(); e.stopPropagation(); setQtyFor(s, q => Math.max(q - 1, 0)); }}
                    >−</button>
                    <span className="ic-qty-val">×{currentQty}</span>
                    <button
                      className="ic-qty-btn"
                      onMouseDown={e => { e.preventDefault(); e.stopPropagation(); setQtyFor(s, q => q + 1); }}
                    >+</button>
                  </div>
                )}
              </div>
            );
          })}
          {scrollHint.below > 0 && (
            <div className="ic-scroll-hint ic-scroll-hint--bottom" aria-hidden="true">
              ▾ {scrollHint.below} más
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
